import React from 'react'
import {
  Alert,
  AlertTitle,
  Collapse,
  Box,
  useTheme,
  Typography,
  SxProps,
  Theme
} from '@mui/material'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlined'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutlineOutlined'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'

export interface FormAlertProps {
  message: string | null
  severity?: 'error' | 'success' | 'info' | 'warning'
  title?: string
  filled?: boolean
  closable?: boolean
  onClose?: () => void
  sx?: SxProps<Theme>
}

const FormAlert: React.FC<FormAlertProps> = ({
  message,
  severity = 'error',
  title,
  filled = false,
  closable = false,
  onClose,
  sx = {}
}) => {
  const theme = useTheme()

  const iconMapping = {
    error: <ErrorOutlineIcon fontSize="inherit" />,
    success: <CheckCircleOutlineIcon fontSize="inherit" />,
    info: <InfoOutlinedIcon fontSize="inherit" />
  }

  return (
    <Box sx={{ width: '100%', mb: message ? 3 : 0, ...sx, py: 1.5, px: 2.5 }}>
      <Collapse in={Boolean(message)}>
        <Alert
          severity={severity}
          variant={filled ? 'filled' : 'standard'}
          iconMapping={iconMapping}
          onClose={closable ? onClose : undefined}
          sx={{
            borderRadius: theme.appRadius.sm,
            alignItems: 'center',
            boxShadow: filled ? theme.shadows[2] : 'none',
            border: !filled ? `1px solid` : 'none',
            ...(!filled &&
              severity === 'error' && {
                bgcolor:
                  theme.palette.mode === 'dark'
                    ? 'rgba(211, 47, 47, 0.1)'
                    : 'rgba(211, 47, 47, 0.05)',
                borderColor:
                  theme.palette.mode === 'dark'
                    ? 'rgba(211, 47, 47, 0.5)'
                    : 'rgba(211, 47, 47, 0.3)',
                color: theme.palette.mode === 'dark' ? '#ffcdd2' : '#d32f2f'
              }),
            ...(!filled &&
              severity === 'success' && {
                bgcolor:
                  theme.palette.mode === 'dark'
                    ? 'rgba(56, 142, 60, 0.1)'
                    : 'rgba(56, 142, 60, 0.05)',
                borderColor:
                  theme.palette.mode === 'dark'
                    ? 'rgba(56, 142, 60, 0.5)'
                    : 'rgba(56, 142, 60, 0.3)'
              })
          }}
        >
          {title && <AlertTitle sx={{ fontWeight: 700 }}>{title}</AlertTitle>}
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {message}
          </Typography>
        </Alert>
      </Collapse>
    </Box>
  )
}

export default FormAlert
